import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
  useDeviceType,
  useDialogHandler,
  useGetTranslation,
} from '@travel/traveler-core/hooks';
import { getAppliedSalesPromotionIds } from '@travel/traveler-core/utils/salesPromotion';
import { Animation, IconTextList } from '@travel/ui';
import { cx, isEmptyArray, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Dialog from 'components/Dialog';
import PhotoGalleryDetail from 'components/PhotoGalleryDetail';
import ShowMorePlansOrRooms, {
  DISPLAY_PLANS_ROOMS_COUNT_PC,
  DISPLAY_PLANS_ROOMS_COUNT_SP,
} from 'components/ShowMorePlansOrRooms';
import { ROOM_DETAIL_QUERY } from 'pages/ProviderInformationPage/constants';
import { getFilteredRoomPlanFeatures } from 'pages/ProviderInformationPage/helpers';

import { getRoomDetail } from 'store/providerRatePlan/selectors';

import { GALLERY_INDEX_QUERY, GALLERY_QUERY, GALLERY_QUERY_VALUE } from 'constants/gallery';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { Translate } from 'core/translate';
import useQueryState from 'hooks/useQueryState';
import useQueryToggle from 'hooks/useQueryToggle';
import { SearchFormQuery } from 'ProviderList-Types';
import { Plan, RoomDetail } from 'ProviderRatePlan-Types';

import RoomBasicInfoSummary from '../RoomBasicInfoSummary';
import RoomPlanItemHeader from '../RoomPlanItemHeader';
import SeeMoreLink from '../SeeMoreLink';
import ItemDetails from './ItemDetails';
import PlanItem from './PlanItem';

import styles from './roomList.module.scss';

type Props = {
  /** Room item id */
  itemId: string;
  /** Provider id */
  providerId: string;
  /** Current search params */
  searchParam: SearchFormQuery;
  /** Callback to be called when date is selected */
  onSelectDate: (startDate?: string, endDate?: string) => void;
  /** is first */
  isFirst: boolean;
};

function RoomInfoHeader(props: { itemId: string; onDisplayRoomDetail?: () => void }) {
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isDesktop = deviceType === DESKTOP_SCREEN;

  const getTranslation = useGetTranslation();

  const roomItem = useSelector(getRoomDetail(props.itemId, getTranslation)) as RoomDetail;

  const { facilities } = roomItem;
  const roomFacilities = getFilteredRoomPlanFeatures(facilities, 0, 6);

  return (
    <div
      className={styles.roomInfoHeaderDetail}
      data-testid="ratePlanList-roomInfoHeaderDetail-wrapper"
    >
      <div className={cx(styles.roomBasicInfo)}>
        <RoomBasicInfoSummary
          bedText={roomItem.bedText}
          roomText={roomItem.roomText}
          smokingAllowed={roomItem.smokingAllowed}
          capacityText={roomItem.capacityText}
          viewText={roomItem.views?.text}
          customStyle="iconText"
        />
      </div>
      {isNotEmptyArray(facilities) && !isMobile && (
        <>
          <Translate
            id="Provider_Info.Basic_Info.Hotel_Info.Room_Facilities"
            className={styles.roomFacilitiesTitle}
          />
          <IconTextList
            className={styles.featureList}
            iconTextSpacing={4}
            items={roomFacilities}
            type={isDesktop ? 'vertical' : 'horizontal'}
            spaceBetween={isDesktop ? 8 : 16}
          />
        </>
      )}
      {!isMobile && (
        <div className={styles.roomSubDetailsBox} data-testid="ratePlan-roomSubDetailBox-wrapper">
          <SeeMoreLink
            title={<Translate id="Provider_Info.Tablet_PC.See_Room_Details" />}
            onClick={props.onDisplayRoomDetail}
          />
        </div>
      )}
    </div>
  );
}

function RoomPlansItem(props: Props) {
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;

  const { itemId, providerId, searchParam, onSelectDate, isFirst } = props;

  const displayPlansRoomsCount = isMobile
    ? DISPLAY_PLANS_ROOMS_COUNT_SP
    : DISPLAY_PLANS_ROOMS_COUNT_PC;
  const [displayCount, setDisplayCount] = useState(displayPlansRoomsCount);

  useEffect(() => {
    isMobile
      ? setDisplayCount(DISPLAY_PLANS_ROOMS_COUNT_SP)
      : setDisplayCount(DISPLAY_PLANS_ROOMS_COUNT_PC);
  }, [isMobile]);

  const getTranslation = useGetTranslation();

  const rateItemRef = useRef<HTMLDivElement | null>(null);

  const {
    isOpen: isOpenRoomDetail,
    onClose: onCloseRoomDetail,
    onOpen: onDisplayRoomDetail,
  } = useDialogHandler(false);

  const {
    isOpen: isOpenRoomDetailMobile,
    onClose: onCloseRoomDetailMobile,
    onOpen: onDisplayRoomDetailMobile,
  } = useQueryToggle(ROOM_DETAIL_QUERY, itemId);

  const { isOpen: isOpenGallery, onClose: onCloseGallery, onOpen: onOpenGallery } = useQueryToggle(
    GALLERY_QUERY,
    `${GALLERY_QUERY_VALUE.PlanDetail}-${itemId}`,
  );
  const [initImageIndex, setInitImageIndex] = useQueryState(GALLERY_INDEX_QUERY, {
    defaultValue: 0,
    parser: parseInt,
  });

  const isOpenDetail = isMobile ? isOpenRoomDetailMobile : isOpenRoomDetail;

  const handleCloseDetail = isMobile ? onCloseRoomDetailMobile : onCloseRoomDetail;

  const handleOpenDetail = isMobile ? onDisplayRoomDetailMobile : onDisplayRoomDetail;

  const handleOpenGallery = (index: number) => {
    onOpenGallery();
    setInitImageIndex(index);
  };

  const onPage: 'providerInfoUndated' | 'providerInfoDated' =
    searchParam?.startDate || searchParam?.endDate ? 'providerInfoDated' : 'providerInfoUndated';

  const { pageType, siteSection, parentName } = RAT_PAGE_TYPE_OBJ[onPage];

  const isDesktop = deviceType === DESKTOP_SCREEN;

  const room = useSelector(getRoomDetail(itemId, getTranslation)) as RoomDetail;

  const visibleRoomPlans = room.plans?.slice(0, displayPlansRoomsCount);
  const invisibleRoomPlans = room.plans?.slice(displayPlansRoomsCount, room.plans.length);

  const renderRoomPlans = (planItem: Plan) => (
    <li
      key={planItem.planId}
      data-ratid={`ratePlanItem_${itemId}`}
      data-testid={`ratePlanItem_${itemId}`}
      data-rat-page_name={parentName}
      data-ratevent="appear"
      data-ratparam="all"
      className={styles.planItem}
    >
      <PlanItem
        key={planItem.itemRatePlanId}
        onPage={onPage}
        displayImage={room.displayImage}
        providerId={providerId}
        itemId={itemId}
        planId={planItem.planId}
        ratePlanId={planItem.itemRatePlanId}
        appliedSalesPromotionIds={
          getAppliedSalesPromotionIds(planItem.price?.salesPromotions) || null
        }
        perRoomPriceBeforeDiscount={planItem.price?.base}
        perRoomPriceAfterDiscount={planItem.price?.total}
        searchParam={searchParam}
        onSelectDate={onSelectDate}
        query={props.searchParam}
      />
    </li>
  );

  if (isEmptyArray(room.plans)) {
    return null;
  }

  return (
    <div
      className={cx(
        styles.room,
        room.plans.length > displayPlansRoomsCount && styles.withShowMoreButton,
      )}
      data-testid="providerInformation-ratePlanList-wrapper"
    >
      <RoomPlanItemHeader
        title={room.name}
        displayImage={room.displayImage}
        media={room.media}
        onDisplayGallery={handleOpenGallery}
        onDisplayDetail={handleOpenDetail}
        isFirst={isFirst}
        infoComponent={<RoomInfoHeader itemId={itemId} onDisplayRoomDetail={handleOpenDetail} />}
      />

      {/* Turn off the translation for the list section: https://jira.rakuten-it.com/jira/browse/TIDP-28464 */}
      <div
        className={styles.planItemsWrapper}
        data-testid="providerInfo-ratePlanList-planItemsWrapper"
        ref={rateItemRef}
        translate="no"
      >
        <ul data-testid="providerInfo-ratePlanList-planItemsBox">
          {visibleRoomPlans.map(renderRoomPlans)}
          <Animation
            isStartAction={displayCount === room.plans.length}
            type="fadeInDown"
            animationDuration="short"
            wrapperClassName={styles.animationWrapper}
          >
            {invisibleRoomPlans.map(renderRoomPlans)}
          </Animation>
        </ul>
        {room.plans.length > displayPlansRoomsCount && (
          <ShowMorePlansOrRooms
            count={room.plans.length}
            displayCount={displayCount}
            setCount={setDisplayCount}
            rateItemRef={rateItemRef.current}
            isShowMorePlan={true}
          />
        )}
      </div>
      {room.displayImage && (
        <PhotoGalleryDetail
          isOpen={isOpenGallery}
          onClose={onCloseGallery}
          imageIndex={initImageIndex}
          mediaData={room.media}
          onChangeIndex={setInitImageIndex}
        />
      )}
      <Dialog
        title={isDesktop ? null : <Translate id="Provider_Info.Room_List.Room_Details.Title" />}
        isOpen={isOpenDetail}
        isSEOContentIndexedOnHidden={true}
        onClose={handleCloseDetail}
        hasEnableBack={false}
        isUseContentFlexBox={false}
        className={styles.dialog}
        contentClassName={styles.dialogContent}
        hasAnimation={true}
      >
        {isDesktop && (
          <h5 className={styles.dialogTitle}>
            <Translate id="Provider_Info.Room_List.Room_Details.Title" />
          </h5>
        )}
        <ItemDetails itemId={itemId} onPage={onPage} />
      </Dialog>
      {isOpenDetail && (
        <DataLayer
          pageName={`${parentName}:room_details_popup`}
          pageType={pageType}
          siteSection={siteSection}
        />
      )}
    </div>
  );
}

// As we have handled the collapse/expand state from parent
// It will make unnecessary re-rendered for other RatePlan component
// (not only for related RatePlan)
export default React.memo(RoomPlansItem);
